import {
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  ModalFooter,
  Button,
  ModalBody,
  Modal,
  ModalHeader,
  Spinner,
} from "reactstrap";
import Select from "react-select";

// import settings.scss
import "./settings.scss";
import { PaymentSettingsValues, SettingsProps } from "../../types/settings";

import { Formik, Form as FormikForm } from "formik";
import { paymentSettingsValidation } from "../../rules/settings";

import { ErrorMessage } from "Components";
import {
  useCreatePayment,
  useDeletePayment,
  useGetPayments,
  useUpdatePayment,
} from "hooks/usePayments";
import { PaymentMethodsTable } from "./components";
import { useEffect, useState } from "react";
import DeleteModal from "Components/Common/DeleteModal";

const PaymentSettings: React.FC<SettingsProps> = ({
  onGlobalCancel,
  isSaving,
}) => {
  const { mutate: createPaymentMethod } = useCreatePayment();
  const { mutate: updatePaymentMethod } = useUpdatePayment();
  const { data: paymentMethods } = useGetPayments();
  const { mutate: deletePaymentMethod } = useDeletePayment();
  const [modal, setModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editDetails, setEditDetails] = useState<any>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<any>(null);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const toggle = () => {
    setModal(!modal);
    if (modal) {
      setEditDetails(null);
    }
  };

  const handleEdit = (data: any) => {
    setEditDetails(data);
    setModal(true);
  };

  const initialValues: PaymentSettingsValues = {
    name: "",
    description: "",
    momoMerchantId: "",
    accountName: "",
    accountNumber: "",
    bankName: "",
    bankBranch: "",
    type: "",
    status: "active",
    provider: "",
    momoNumber: "",
  };

  const customSelectStyle = {
    control: (provided: any) => ({
      ...provided,
      backgroundColor: "white",
      borderColor: "#ced4da",
      boxShadow: "none",
      minHeight: "38px",
      "&:hover": {
        borderColor: "#80bdff",
      },
    }),
    menu: (provided: any) => ({
      ...provided,
      zIndex: 2,
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: "#6c757d",
    }),
  };

  useEffect(() => {
    if (editDetails) {
      initialValues.name = editDetails.name || "";
      initialValues.description = editDetails.description || "";
      initialValues.momoMerchantId = editDetails.payment_info.merchant_id || "";
      initialValues.accountName = editDetails.payment_info.account_name || "";
      initialValues.accountNumber =
        editDetails.payment_info.account_number || "";
      initialValues.bankName = editDetails.payment_info.bank_name || "";
      initialValues.bankBranch = editDetails.payment_info.bank_branch || "";
      initialValues.type = editDetails.type || "";
      initialValues.status = editDetails.status || "active";
      initialValues.provider = editDetails.payment_info.provider || "";
      initialValues.momoNumber = editDetails.payment_info.momo_number || "";
    }
  }, [editDetails]);

  const handleSave = async (values: PaymentSettingsValues) => {
    let payload = {
      name: values.name,
      description: values.description,
      type: values.type,
      status: values.status,
      payment_info: {},
    };
    if (values.type === "momo") {
      payload.payment_info = {
        momo_number: values.momoNumber,
        merchant_id: values.momoMerchantId,
        provider: values.provider,
      };
    }
    if (values.type === "bank") {
      payload.payment_info = {
        account_name: values.accountName,
        account_number: values.accountNumber,
        bank_name: values.bankName,
        bank_branch: values.bankBranch,
      };
    }
    if (editDetails) {
      setIsLoading(true);

      updatePaymentMethod(
        { id: editDetails.id, data: payload },
        {
          onSuccess: () => {
            toggle();
            setIsLoading(false);
          },
          onError: () => {
            setIsLoading(false);
          },
        }
      );
    } else {
      setIsLoading(true);
      createPaymentMethod(payload, {
        onSuccess: () => {
          toggle();
          setIsLoading(false);
        },
        onError: () => {
          setIsLoading(false);
        },
      });
    }
  };
  const paymentTypes = [
    { value: "bank", label: "Bank" },
    { value: "cash", label: "Cash" },
    { value: "momo", label: "Mobile Money" },
  ];

  const providers = [
    { value: "MTN", label: "MTN Mobile Money" },
    { value: "Telecel", label: "Telecel Cash" },
    {
      value: "Airtel Tigo",
      label: "AirtelTigo Money",
    },
  ];

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  // Delete functionality
  const onClickDelete = (data: any) => {
    setSelectedPaymentMethod(data);
    console.log("Payment:", data);
    setDeleteModal(true);
  };

  const handleDeletePaymentMethod = () => {
    if (selectedPaymentMethod.id) {
      deletePaymentMethod(selectedPaymentMethod.id);
      setDeleteModal(false);
    }
  };

  return (
    <>
      <div className="bg-transparent">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="mb-0">Payment Settings</h4>
          <Button color="primary" onClick={toggle}>
            <i className="bx bx-plus me-1"></i> Add payment method
          </Button>
        </div>

        <div>
          <PaymentMethodsTable
            data={paymentMethods}
            onDeleteItemClick={onClickDelete}
            onEditItemClick={handleEdit}
          />
        </div>

        <Modal isOpen={modal} toggle={toggle} centered size="md">
          <Formik
            initialValues={initialValues}
            validationSchema={paymentSettingsValidation}
            onSubmit={handleSave}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              setFieldValue,
              handleSubmit,
            }) => (
              <>
                <ModalHeader toggle={toggle} className="border-1 pb-2 mb-2">
                  <h4 className="mb-0">
                    {editDetails ? "Edit Payment Method" : "Add Payment Method"}
                  </h4>
                </ModalHeader>
                <ModalBody className="pt-2">
                  <FormikForm>
                    <div>
                      <Row>
                        <Col md={6}>
                          <FormGroup>
                            <Label for="name">Payment method name</Label>
                            <Input
                              type="text"
                              id="name"
                              name="name"
                              placeholder="Enter Payment method name(eg. Bank)"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.name}
                              invalid={!!(touched.name && errors.name)}
                            />
                            {touched.name && errors.name && (
                              <FormFeedback>{errors.name}</FormFeedback>
                            )}
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label for="type">Payment type</Label>
                            <Select
                              id="type"
                              name="type"
                              value={paymentTypes?.find(
                                (item) => item.value === values.type
                              )}
                              onChange={(option) =>
                                setFieldValue("type", option?.value)
                              }
                              options={paymentTypes}
                              placeholder="Select payment type"
                              styles={customSelectStyle}
                            />

                            {touched.type && errors.type && (
                              <ErrorMessage>{errors.type}</ErrorMessage>
                            )}
                          </FormGroup>
                        </Col>
                      </Row>
                    </div>
                    <div className="mt-2">
                      <Row>
                        <Col md={6}>
                          <FormGroup>
                            <Label for="description">Description</Label>
                            <Input
                              type="text"
                              id="description"
                              description="description"
                              placeholder="Description"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.description}
                              invalid={
                                !!(touched.description && errors.description)
                              }
                            />
                            {touched.description && errors.description && (
                              <FormFeedback>{errors.description}</FormFeedback>
                            )}
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label for="type">Status</Label>
                            <Select
                              id="payment_status"
                              name="status"
                              value={statusOptions?.find(
                                (item) => item.value === values.status
                              )}
                              onChange={(option) =>
                                setFieldValue("status", option?.value)
                              }
                              options={statusOptions}
                              placeholder="Select status"
                              styles={customSelectStyle}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                    </div>
                    {values?.type === "momo" && (
                      <div className="mt-2">
                        <h6 className="mb-3">Mobile Money Settings</h6>
                        <Row className="g-3">
                          <Col md={6}>
                            <FormGroup>
                              <Label for="momoMerchantId">Merchant ID</Label>
                              <Input
                                type="text"
                                id="momoMerchantId"
                                name="momoMerchantId"
                                placeholder="Enter merchant ID"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.momoMerchantId}
                                invalid={
                                  !!(
                                    touched.momoMerchantId &&
                                    errors.momoMerchantId
                                  )
                                }
                              />
                            </FormGroup>
                          </Col>

                          <Col md={6}>
                            <FormGroup>
                              <Label for="momoNumber">
                                Mobile Money Number
                              </Label>
                              <Input
                                type="text"
                                id="momoNumber"
                                name="momoNumber"
                                placeholder="Enter mobile money number"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.momoNumber}
                                invalid={
                                  !!(touched.momoNumber && errors.momoNumber)
                                }
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label for="provider">Provider</Label>
                              <Select
                                id="provider"
                                name="provider"
                                value={providers?.find(
                                  (item) => item.value === values.provider
                                )}
                                onChange={(option) =>
                                  setFieldValue("provider", option?.value)
                                }
                                options={providers}
                                placeholder="Select provider"
                                styles={customSelectStyle}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                      </div>
                    )}
                    {values?.type === "bank" && (
                      <div className="mt-4">
                        <h6 className="mb-3">Bank Transfer Settings</h6>
                        <Row className="g-3">
                          <Col md={6}>
                            <FormGroup>
                              <Label for="accountName">Account Name</Label>
                              <Input
                                type="text"
                                id="accountName"
                                name="accountName"
                                placeholder="Enter account name"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.accountName}
                                invalid={
                                  !!(touched.accountName && errors.accountName)
                                }
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label for="accountNumber">Account Number</Label>
                              <Input
                                type="text"
                                id="accountNumber"
                                name="accountNumber"
                                placeholder="Enter account number"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.accountNumber}
                                invalid={
                                  !!(
                                    touched.accountNumber &&
                                    errors.accountNumber
                                  )
                                }
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label for="bankName">Bank Name</Label>
                              <Input
                                type="text"
                                id="bankName"
                                name="bankName"
                                placeholder="Enter bank name"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.bankName}
                                invalid={
                                  !!(touched.bankName && errors.bankName)
                                }
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label for="bankBranch">Bank Branch</Label>
                              <Input
                                type="text"
                                id="bankBranch"
                                name="bankBranch"
                                placeholder="Enter bank branch"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.bankBranch}
                                invalid={
                                  !!(touched.bankBranch && errors.bankBranch)
                                }
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                      </div>
                    )}
                  </FormikForm>
                </ModalBody>
                <ModalFooter className="border-1 pt-0">
                  <Button
                    color="secondary"
                    onClick={toggle}
                    className="btn-soft-secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    className="w-sm"
                    color="primary"
                    onClick={() => handleSubmit()}
                    size="md"
                  >
                    {isLoading ? (
                      <Spinner size="sm" />
                    ) : editDetails ? (
                      "Update"
                    ) : (
                      "Add"
                    )}
                  </Button>
                </ModalFooter>
              </>
            )}
          </Formik>
        </Modal>
        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDeletePaymentMethod}
          onCloseClick={() => setDeleteModal(false)}
        />
      </div>
    </>
  );
};

export default PaymentSettings;
